import { api } from '@/api/ApiGateway'
import { useAuthStore } from '@/stores/authStore'
import { useTwoFactorVerificationStore } from '@/stores/twoFactorVerificationStore'
import { throttle } from 'lodash/function.js'
import { systemEchoes } from '@/composables/echo/useSystemEchoes.js'

class UserApi
{
	constructor()
	{
		const refreshInterval = (Number(import.meta.env.VITE_USER_DATA_REFRESH_INTERVAL_SECONDS ?? 60)) * 1000 // 60 seconds, 60000 millis
		this.throttledRefreshUserData = throttle(this.refreshUserData, refreshInterval, {
			trailing: false
		});
	}

	login(email, password, tenantId, onSuccess, onError)
	{
		api.post('users/login', {
			'user': {
				'email': email,
				'password': password,
				'tenant_id': tenantId,
				'tfa_token': useTwoFactorVerificationStore().tfaToken //Used to match login to tfa token (for trust device)
			}
		}).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			try
			{
				const shouldNavigate = useAuthStore()?.handleLoginResult(response.body(), email, password)
				onSuccess(shouldNavigate)
			}
			catch (e)
			{
				console.log(e)
				response.overrideError({ detail: `${e}` })
				onError(response)
			}
		})
	}

	logout() {
		try
		{
			api.get('users/logout').then(() => {})
		}
		catch(e)
		{
			console.log(e, 'Error Logging out.');
		}
	}

	register(email, password, password_confirmation, t_and_c_checkbox, token, onSuccess, onError)
	{
		api.post('users/register', {
			'user': {
				'email': email,
				'password': password,
				'password_confirmation': password_confirmation,
				't_and_c': t_and_c_checkbox
			},
			'token': token
		}).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			try
			{
				const shouldNavigate = useAuthStore()?.handleLoginResult(response.body(), email, password)
				onSuccess(shouldNavigate)
			}
			catch (e)
			{
				response.overrideError({ detail: `${e}` })
				onError(response)
			}
		})
	}

	update(userData, onSuccess, onError)
	{
		api.patch('users/my-user', userData).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			try
			{
				onSuccess(response)
			}
			catch (e)
			{
				response.overrideError({ detail: `${e}` })
				onError(response)
			}
		})
	}

	getInvite(token, onSuccess, onError)
	{
		api.get(`users/invite/${token}`).then(response =>
		{
			if (response.hasError())
			{
				onError(response)
				return
			}
			if (!response.body()?.data)
			{
				response.overrideError({ detail: 'Error loading user invite.' })
				onError(response)
				return
			}

			onSuccess(response.body()?.data)
		})
	}

	forgotPassword(user, onSuccess, onError) {
		api.post('users/forgot-password', user).then(response => {
			if (response.hasError()) {
				onError(response)
				return
			}

			onSuccess(response)
		})
	}

	resetPassword(user, onSuccess, onError) {
		api.post('users/reset-password', user).then(response => {
			if (response.hasError()) {
				onError(response)
				return
			}

			if (!response.body()?.data) {
				response.overrideError({ detail: 'Error resetting user password.' })
				onError(response)
				return
			}

			onSuccess(response)
		})
	}

	verifyTfa(user, onSuccess, onError) {
		api.post('users/tfa/verify', user).then(response => {
			if (response.hasError()) {
				onError(response)
				return
			}

			if (!response.body()?.data) {
				response.overrideError({ detail: 'Error resetting user password.' })
				onError(response)
				return
			}

			onSuccess(response.body())
		})
	}

	getMyUser(onComplete) {
		//This calls a special api.get_for_reset function so ApiGateway doesn't get in an infinite loop
		api.get_for_reset('users/my-user', null).then(response => {
			onComplete(response)
		})
	}

	/**
	 * Checks if a user's data has expired or forced refresh set.
	 */
	refreshUserData()
	{
		const authStore = useAuthStore()
		if (!authStore.isLoggedIn) return

		systemEchoes.notificationRoleDisconnect();

		userApi.getMyUser((response) => {
			if (response.hasError()) {
				return
			}

			const oldActiveOrgId = authStore.activeOrganizationId;
			authStore.handleLoginResult(response.body(), null, null);

			const oldActiveOrgExists = authStore.getUserOrganizationData?.organizations?.find((org) => {
				return org.id === oldActiveOrgId;
			});

			if (oldActiveOrgExists) {
				authStore.setActiveOrganizationId(oldActiveOrgId);
			}

			systemEchoes.notificationRoleConnect();
		});
	}

	getTenantByHostname(hostname, onComplete) {
		api.get(`users/tenant/${hostname}`).then(response => {
			onComplete(response.body()['data'])
		})
	}

	getTenantById(id, onComplete) {
		api.get(`users/tenant-by-id/${id}`).then(response => {
			onComplete(response.body())
		})
	}

	updateTenantSettings(settingsJson, onSuccess, onError) {
		api.patch(`users/tenant-settings`, settingsJson).then(response => {
			if (response.hasError()) {
				onError(response)
				return
			}

			if (!response.body()?.data) {
				response.overrideError({ detail: 'Error updating tenant settings.' })
				onError(response)
				return
			}

			onSuccess(response.body())
		})
	}

	updateTenantModuleSettings(modules, onSuccess, onError) {
		api.post(`users/tenant-module-settings`, modules).then(response => {
			if (response.hasError()) {
				onError(response)
				return
			}

			if (!response.body()?.data) {
				response.overrideError({ detail: 'Error updating tenant module settings.' })
				onError(response)
				return
			}

			onSuccess(response.body())
		})
	}

	getTenantLoginMessage(tenantId, onComplete) {
		api.get(`users/tenant/${tenantId}/login-message`).then(response => {
			onComplete(response.body())
		})
	}
}

export const userApi = new UserApi()